import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { combineLatest, Subscription, interval } from 'rxjs';
import { HoursTrackerService } from './hours-tracker.service';
import { PayPeriod, Shift } from './hours-tracker.model';

export interface PayPeriodWithShifts extends PayPeriod {
  shifts: Shift[];
  totalHours: number;
  isOpen: boolean;
}

@Component({
    selector: 'app-hours-tracker',
    templateUrl: './hours-tracker.component.html',
    styleUrls: ['./hours-tracker.component.scss'],
    standalone: false
})
export class HoursTrackerComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  loading = true;
  payPeriods: PayPeriodWithShifts[] = [];
  activeShift: Shift | null = null;
  elapsedTime = '';
  todayHasPeriod = false;

  showShiftModal = false;
  editingShift: Shift | null = null;
  shiftForm = { periodId: '', date: '', clockIn: '', clockOut: '' };
  shiftFormError = '';

  showPeriodModal = false;
  editingPeriod: PayPeriod | null = null;
  periodForm = { startDate: '', endDate: '', label: '' };
  periodFormError = '';

  private subs: Subscription[] = [];
  private elapsedSub?: Subscription;
  private ensuredPeriodKey: string | null = null;
  private allShifts: Shift[] = [];

  constructor(
    private auth: AngularFireAuth,
    private hoursService: HoursTrackerService
  ) {}

  ngOnInit() {
    const dataSub = combineLatest([
      this.auth.user,
      this.hoursService.getPayPeriods(),
      this.hoursService.getShifts()
    ]).subscribe(([user, periods, shifts]) => {
      this.isAuthenticated = !!user;

      if (user) {
        this.ensureCurrentPeriod(periods);
      }

      const today = this.localDateString(new Date());

      this.allShifts = shifts;
      this.activeShift = shifts.find(s => s.clockOut == null) ?? null;
      this.startElapsedTimer();

      this.payPeriods = periods
        .sort((a, b) => b.startDate.localeCompare(a.startDate))
        .map(period => {
          const periodShifts = shifts
            .filter(s => s.periodId === period.id && s.clockOut != null)
            .sort((a, b) => b.clockIn.localeCompare(a.clockIn));
          const totalHours = this.calcTotalHours(periodShifts);
          const isCurrent = today >= period.startDate && today <= period.endDate;
          return { ...period, shifts: periodShifts, totalHours, isOpen: isCurrent };
        });

      if (this.payPeriods.length && !this.payPeriods.some(p => p.isOpen)) {
        this.payPeriods[0].isOpen = true;
      }

      this.todayHasPeriod = this.payPeriods.some(p => today >= p.startDate && today <= p.endDate);
      this.loading = false;
    });

    this.subs.push(dataSub);
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
    this.elapsedSub?.unsubscribe();
  }

  private localDateString(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private currentPeriodBounds(d: Date): { startDate: string, endDate: string } {
    const y = d.getFullYear();
    const m = d.getMonth();
    if (d.getDate() <= 15) {
      return {
        startDate: this.localDateString(new Date(y, m, 1)),
        endDate: this.localDateString(new Date(y, m, 15))
      };
    }
    const lastDay = new Date(y, m + 1, 0).getDate();
    return {
      startDate: this.localDateString(new Date(y, m, 16)),
      endDate: this.localDateString(new Date(y, m, lastDay))
    };
  }

  private ensureCurrentPeriod(periods: PayPeriod[]) {
    const bounds = this.currentPeriodBounds(new Date());
    const key = `${bounds.startDate}_${bounds.endDate}`;
    if (this.ensuredPeriodKey === key) return;
    this.ensuredPeriodKey = key;
    const exists = periods.some(p => p.startDate === bounds.startDate && p.endDate === bounds.endDate);
    if (!exists) {
      this.hoursService.addPayPeriod(bounds);
    }
  }

  private startElapsedTimer() {
    this.elapsedSub?.unsubscribe();
    if (!this.activeShift) { this.elapsedTime = ''; return; }
    const update = () => {
      const ms = Date.now() - new Date(this.activeShift!.clockIn).getTime();
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      this.elapsedTime = h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`;
    };
    update();
    this.elapsedSub = interval(60000).subscribe(update);
  }

  togglePeriod(period: PayPeriodWithShifts) {
    period.isOpen = !period.isOpen;
  }

  calcShiftHours(shift: Shift): number {
    if (!shift.clockOut) return 0;
    const ms = new Date(shift.clockOut).getTime() - new Date(shift.clockIn).getTime();
    return Math.round((ms / 3600000) * 100) / 100;
  }

  private calcTotalHours(shifts: Shift[]): number {
    return Math.round(shifts.reduce((sum, s) => sum + this.calcShiftHours(s), 0) * 100) / 100;
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }

  formatPeriodRange(period: PayPeriod): string {
    const fmt = (s: string) => {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString([], { month: 'short', day: 'numeric' });
    };
    const [y, m, d] = period.endDate.split('-').map(Number);
    const endFmt = new Date(y, m - 1, d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(period.startDate)} – ${endFmt}`;
  }

  clockIn() {
    const today = this.localDateString(new Date());
    const period = this.payPeriods.find(p => today >= p.startDate && today <= p.endDate);
    if (!period) return;
    this.hoursService.clockIn(period.id);
  }

  clockOut() {
    if (!this.activeShift) return;
    this.hoursService.clockOut(this.activeShift.id);
  }

  openAddShift(period: PayPeriodWithShifts) {
    this.editingShift = null;
    this.shiftForm = { periodId: period.id, date: '', clockIn: '', clockOut: '' };
    this.shiftFormError = '';
    this.showShiftModal = true;
  }

  openEditShift(shift: Shift) {
    this.editingShift = shift;
    const d = new Date(shift.clockIn);
    const toTimeInput = (iso: string) => {
      const t = new Date(iso);
      return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    };
    this.shiftForm = {
      periodId: shift.periodId,
      date: this.localDateString(d),
      clockIn: toTimeInput(shift.clockIn),
      clockOut: shift.clockOut ? toTimeInput(shift.clockOut) : ''
    };
    this.shiftFormError = '';
    this.showShiftModal = true;
  }

  closeShiftModal() {
    this.showShiftModal = false;
    this.editingShift = null;
  }

  saveShift() {
    const { periodId, date, clockIn, clockOut } = this.shiftForm;
    if (!date || !clockIn || !clockOut) {
      this.shiftFormError = 'All fields are required.';
      return;
    }
    const period = this.payPeriods.find(p => p.id === periodId);
    if (period && (date < period.startDate || date > period.endDate)) {
      this.shiftFormError = `Date must fall within the pay period (${this.formatPeriodRange(period)}).`;
      return;
    }
    const clockInISO = new Date(`${date}T${clockIn}:00`).toISOString();
    const clockOutISO = new Date(`${date}T${clockOut}:00`).toISOString();
    if (new Date(clockOutISO) <= new Date(clockInISO)) {
      this.shiftFormError = 'Clock out must be after clock in.';
      return;
    }
    const data = { periodId, clockIn: clockInISO, clockOut: clockOutISO };
    const op = this.editingShift
      ? this.hoursService.updateShift(this.editingShift.id, data)
      : this.hoursService.saveShift(data);
    op.then(() => this.closeShiftModal())
      .catch(() => { this.shiftFormError = 'Save failed. Please try again.'; });
  }

  deleteShift() {
    if (!this.editingShift) return;
    this.hoursService.deleteShift(this.editingShift.id)
      .then(() => this.closeShiftModal())
      .catch(() => { this.shiftFormError = 'Delete failed. Please try again.'; });
  }

  openAddPeriod() {
    this.editingPeriod = null;
    this.periodForm = { startDate: '', endDate: '', label: '' };
    this.periodFormError = '';
    this.showPeriodModal = true;
  }

  openEditPeriod(period: PayPeriod) {
    this.editingPeriod = period;
    this.periodForm = {
      startDate: period.startDate,
      endDate: period.endDate,
      label: period.label || ''
    };
    this.periodFormError = '';
    this.showPeriodModal = true;
  }

  closePeriodModal() {
    this.showPeriodModal = false;
    this.editingPeriod = null;
  }

  savePeriod() {
    const { startDate, endDate, label } = this.periodForm;
    if (!startDate || !endDate) {
      this.periodFormError = 'Start and end dates are required.';
      return;
    }
    if (endDate <= startDate) {
      this.periodFormError = 'End date must be after start date.';
      return;
    }
    const op = this.editingPeriod
      ? this.hoursService.updatePayPeriod(
          this.editingPeriod.id,
          { startDate, endDate, label: (label || null) as any }
        )
      : this.hoursService.addPayPeriod({ startDate, endDate, ...(label ? { label } : {}) });
    op.then(() => this.closePeriodModal())
      .catch(() => { this.periodFormError = 'Save failed. Please try again.'; });
  }

  deletePeriod() {
    if (!this.editingPeriod) return;
    const periodId = this.editingPeriod.id;
    const periodShifts = this.allShifts.filter(s => s.periodId === periodId);
    const msg = periodShifts.length
      ? `Delete this pay period and its ${periodShifts.length} shift${periodShifts.length === 1 ? '' : 's'}?`
      : 'Delete this pay period?';
    if (!confirm(msg)) return;
    Promise.all(periodShifts.map(s => this.hoursService.deleteShift(s.id)))
      .then(() => this.hoursService.deletePayPeriod(periodId))
      .then(() => this.closePeriodModal())
      .catch(() => { this.periodFormError = 'Delete failed. Please try again.'; });
  }
}
